import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useShipmentEmailFeed,
  useShipmentFeed,
  useShipmentFileFeed,
  useShipmentNoteFeed,
  useShipmentPaymentFeed,
  useShipmentSmsFeed,
  useShipmentTaskFeed,
  useShipmentToolbarCounts,
} from '#/entities/shipment'
import type { ToolbarCounts } from '#/shared/api/generated/shipment/models'
import { cn } from '#/shared/lib/utils'
import {
  CheckRoundedIcon,
  EditIcon,
  EmailIcon,
  FileIcon,
  HistoryIcon,
  PaymentIcon,
  PhoneIcon,
} from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'
import { FeedAutoLoader } from '#/widgets/shipment-activity-feed'

import {
  emailEntry,
  fileEntry,
  noteEntry,
  paymentEntry,
  smsEntry,
  taskEntry,
  toSidebarFeedEntry,
} from './sidebar-feed-entries'
import type { SidebarFeedEntryData } from './sidebar-feed-entries'
import { SidebarFeedList } from './sidebar-feed'

export const ACTIVITY_FILTER = {
  all: 'all',
  notes: 'notes',
  tasks: 'tasks',
  sms: 'sms',
  email: 'email',
  files: 'files',
  payments: 'payments',
} as const

export type ActivityFilter =
  (typeof ACTIVITY_FILTER)[keyof typeof ACTIVITY_FILTER]

const ACTIVITY_FILTERS = [
  ACTIVITY_FILTER.all,
  ACTIVITY_FILTER.notes,
  ACTIVITY_FILTER.tasks,
  ACTIVITY_FILTER.sms,
  ACTIVITY_FILTER.email,
  ACTIVITY_FILTER.files,
  ACTIVITY_FILTER.payments,
] as const

// `all` has no count (matches the full-detail history accordion); every other
// chip maps to a ToolbarCounts key.
const COUNT_KEY_BY_FILTER: Record<
  Exclude<ActivityFilter, 'all'>,
  keyof ToolbarCounts
> = {
  [ACTIVITY_FILTER.notes]: 'note',
  [ACTIVITY_FILTER.tasks]: 'task',
  [ACTIVITY_FILTER.sms]: 'sms',
  [ACTIVITY_FILTER.email]: 'email',
  [ACTIVITY_FILTER.files]: 'file',
  [ACTIVITY_FILTER.payments]: 'payment',
}

type IconComponent = React.ComponentType<{ className?: string }>

const EMPTY_BY_FILTER: Record<
  ActivityFilter,
  { icon: IconComponent; kind: string }
> = {
  [ACTIVITY_FILTER.all]: { icon: HistoryIcon, kind: 'all' },
  [ACTIVITY_FILTER.notes]: { icon: EditIcon, kind: 'note' },
  [ACTIVITY_FILTER.tasks]: { icon: CheckRoundedIcon, kind: 'task' },
  [ACTIVITY_FILTER.sms]: { icon: PhoneIcon, kind: 'phone' },
  [ACTIVITY_FILTER.email]: { icon: EmailIcon, kind: 'email' },
  [ACTIVITY_FILTER.files]: { icon: FileIcon, kind: 'file' },
  [ACTIVITY_FILTER.payments]: { icon: PaymentIcon, kind: 'payment' },
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count?: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.currentTarget.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        })
        onClick()
      }}
      className={cn(
        'flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs select-none',
        active
          ? 'bg-gray-900 font-medium text-white'
          : 'border border-gray-200 bg-warm-50 text-gray-500 hover:text-gray-900',
      )}
    >
      {label}
      {count != null && count > 0 && (
        <span className={active ? 'text-white/60' : 'text-gray-400'}>
          ({count})
        </span>
      )}
    </button>
  )
}

function ActivityFilterChips({
  value,
  onValueChange,
  counts,
}: {
  value: ActivityFilter
  onValueChange: (next: ActivityFilter) => void
  counts: ToolbarCounts | undefined
}) {
  const { t } = useTranslation()
  return (
    <div className="no-scrollbar flex gap-2 overflow-x-auto px-5 py-3">
      {ACTIVITY_FILTERS.map((filter) => (
        <FilterChip
          key={filter}
          label={t(`detailSidebar.activityFilters.${filter}`)}
          count={
            filter === ACTIVITY_FILTER.all
              ? undefined
              : counts?.[COUNT_KEY_BY_FILTER[filter]]
          }
          active={value === filter}
          onClick={() => onValueChange(filter)}
        />
      ))}
    </div>
  )
}

function FeedSkeleton() {
  return (
    <div className="flex w-full flex-col gap-4 pt-2">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-start gap-3">
          <Skeleton className="size-8 shrink-0 rounded-lg" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

function FilterEmpty({ filter }: { filter: ActivityFilter }) {
  const { t } = useTranslation()
  const { icon: Icon, kind } = EMPTY_BY_FILTER[filter]
  return (
    <div className="flex animate-in fade-in slide-in-from-top-1 flex-col items-center gap-2 px-5 py-6 text-center duration-300 ease-out">
      <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        <Icon className="size-5" />
      </div>
      <p className="text-sm font-medium text-gray-900">
        {t(`orderDetails.history.empty.${kind}.title`)}
      </p>
      <p className="text-xs text-gray-500">
        {t(`orderDetails.history.empty.${kind}.hint`)}
      </p>
    </div>
  )
}

function FilteredFeed({
  filter,
  entries,
  isLoading,
}: {
  filter: ActivityFilter
  entries: SidebarFeedEntryData[]
  isLoading: boolean
}) {
  if (isLoading) return <FeedSkeleton />
  if (entries.length === 0) return <FilterEmpty filter={filter} />
  return <SidebarFeedList entries={entries} />
}

function AllFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { items, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useShipmentFeed(shipmentId)
  const entries = items.map((item) => toSidebarFeedEntry(t, item))
  if (isLoading) return <FeedSkeleton />
  if (entries.length === 0) return <FilterEmpty filter={ACTIVITY_FILTER.all} />
  return (
    <>
      <SidebarFeedList entries={entries} />
      <FeedAutoLoader
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
      />
    </>
  )
}

function NotesFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { notes, isLoading } = useShipmentNoteFeed(shipmentId)
  return (
    <FilteredFeed
      filter={ACTIVITY_FILTER.notes}
      entries={notes.map((note) => noteEntry(t, note))}
      isLoading={isLoading}
    />
  )
}

function TasksFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { tasks, isLoading } = useShipmentTaskFeed(shipmentId)
  return (
    <FilteredFeed
      filter={ACTIVITY_FILTER.tasks}
      entries={tasks.map((task) => taskEntry(t, task))}
      isLoading={isLoading}
    />
  )
}

function SmsFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { messages, isLoading } = useShipmentSmsFeed(shipmentId)
  return (
    <FilteredFeed
      filter={ACTIVITY_FILTER.sms}
      entries={messages.map((message) => smsEntry(t, message))}
      isLoading={isLoading}
    />
  )
}

function EmailFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { emails, isLoading } = useShipmentEmailFeed(shipmentId)
  return (
    <FilteredFeed
      filter={ACTIVITY_FILTER.email}
      entries={emails.map((email) => emailEntry(t, email))}
      isLoading={isLoading}
    />
  )
}

function FilesFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { files, isLoading } = useShipmentFileFeed(shipmentId)
  return (
    <FilteredFeed
      filter={ACTIVITY_FILTER.files}
      entries={files.map((file) => fileEntry(t, file))}
      isLoading={isLoading}
    />
  )
}

function PaymentsFeed({ shipmentId }: { shipmentId: string }) {
  const { t } = useTranslation()
  const { payments, isLoading } = useShipmentPaymentFeed(shipmentId)
  return (
    <FilteredFeed
      filter={ACTIVITY_FILTER.payments}
      entries={payments.map((payment) => paymentEntry(t, payment))}
      isLoading={isLoading}
    />
  )
}

const FEED_BY_FILTER: Record<
  Exclude<ActivityFilter, 'all'>,
  (shipmentId: string) => React.ReactNode
> = {
  [ACTIVITY_FILTER.notes]: (id) => <NotesFeed shipmentId={id} />,
  [ACTIVITY_FILTER.tasks]: (id) => <TasksFeed shipmentId={id} />,
  [ACTIVITY_FILTER.sms]: (id) => <SmsFeed shipmentId={id} />,
  [ACTIVITY_FILTER.email]: (id) => <EmailFeed shipmentId={id} />,
  [ACTIVITY_FILTER.files]: (id) => <FilesFeed shipmentId={id} />,
  [ACTIVITY_FILTER.payments]: (id) => <PaymentsFeed shipmentId={id} />,
}

export function SidebarActivityTab({ shipmentId }: { shipmentId: string }) {
  const [filter, setFilter] = useState<ActivityFilter>(ACTIVITY_FILTER.all)
  const { counts } = useShipmentToolbarCounts(shipmentId)
  return (
    <div className="flex flex-col">
      <ActivityFilterChips
        value={filter}
        onValueChange={setFilter}
        counts={counts}
      />
      <div className="px-5 pt-1 pb-6">
        {filter === ACTIVITY_FILTER.all ? (
          <AllFeed shipmentId={shipmentId} />
        ) : (
          FEED_BY_FILTER[filter](shipmentId)
        )}
      </div>
    </div>
  )
}
